/**
 *  @class
 *  @function FreeShipping
 */

if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.amountText = this.querySelector('.free-shipping--text span');
      this.minimum = Math.round(parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1));
      this.initialCartTotal = parseInt(this.dataset.cartTotal, 10);
      this.isWholesale = this.dataset.customerType === 'wholesale';
      this.remainingText = this.querySelector('.free-shipping--text-remaining');
      this.fullText = this.querySelector('.free-shipping--text-full');
      this.updateQueued = false;
      this.updateTimer = null;
      this.releaseCalculatingTimer = null;
      this.lastTotal = null;
      this.lastRemaining = null;
      this.lastQualified = null;
      this.refreshTimers = [];

      this.update();
      this.observeCartTotal();

      if (this.isWholesale) {
        this.waitForWholesaleDiscount();
      }

      this.refreshTimers = [250, 750, 1500, 3000].map((delay) => {
        return setTimeout(() => {
          let total = this.update();
          this.releaseCalculatingIfReady(total);
        }, delay);
      });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      if (this.releaseCalculatingTimer) {
        clearTimeout(this.releaseCalculatingTimer);
      }
      this.refreshTimers.forEach((timer) => clearTimeout(timer));
    }

    getCartContainer() {
      return this.closest('cart-drawer') || this.closest('#Cart') || document;
    }

    getCartTotalSource() {
      let container = this.getCartContainer();
      let totalSource = container.querySelector('[data-cart-total-source]') || container.querySelector('.cart-total .amount');

      return totalSource ? totalSource.closest('.cart-total') || totalSource : null;
    }

    parseMoney(text) {
      if (!text) return null;

      let matches = text.replace(/\s/g, '').match(/-?\d[\d,.' ]*(?:\.\d{1,2}|,\d{1,2})?/g);
      if (!matches) return null;

      let value = matches[matches.length - 1];
      let decimalSeparator = '.';
      let lastDot = value.lastIndexOf('.');
      let lastComma = value.lastIndexOf(',');

      if (lastComma > lastDot && value.length - lastComma <= 3) {
        decimalSeparator = ',';
      }

      value = value.replace(new RegExp(`[^0-9\\${decimalSeparator}-]`, 'g'), '');

      if (decimalSeparator === ',') {
        value = value.replace(',', '.');
      }

      let amount = Number(value);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    }

    getFinalTotalFromElement(element) {
      if (!element) return null;

      let clone = element.cloneNode(true);
      clone.querySelectorAll('del, s, strike').forEach((node) => node.remove());

      let preferredAmount = clone.querySelector('.mysubb, ins .amount, .amount.discounted, .price--end, [data-final-price], [data-discounted-price]');

      if (!preferredAmount) {
        preferredAmount = Array.from(clone.querySelectorAll('.money, .amount')).find((node) => {
          return !node.closest('del, s, strike');
        });
      }

      let amount = preferredAmount ? this.parseMoney(preferredAmount.textContent) : null;

      if (amount !== null) {
        return amount;
      }

      return this.parseMoney(clone.textContent);
    }

    getLineItemsTotal() {
      let container = this.getCartContainer();
      let itemsSource = container.querySelector('[data-cart-items-source]');
      let priceElements = itemsSource ? itemsSource.querySelectorAll('[data-cart-line-source]') : container.querySelectorAll('.product-cart-item--container > .product-cart-item .product-cart-item-price');

      if (!priceElements.length) {
        return null;
      }

      let total = 0;
      let pricedItems = 0;

      priceElements.forEach((priceElement) => {
        let price = this.getFinalTotalFromElement(priceElement);

        if (price !== null) {
          total += price;
          pricedItems += 1;
        }
      });

      return pricedItems === priceElements.length ? total : null;
    }

    getCurrentTotal() {
      let lineItemsTotal = this.getLineItemsTotal();
      if (lineItemsTotal !== null) {
        return lineItemsTotal;
      }

      let totalSource = this.getCartTotalSource();
      let displayedTotal = this.getFinalTotalFromElement(totalSource);

      return displayedTotal !== null ? displayedTotal : this.initialCartTotal;
    }

    isRelevantMutationNode(node) {
      if (!node || node === this || this.contains(node)) return false;

      let element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (!element) return false;

      return !!element.closest('[data-cart-items-source], [data-cart-line-source], .product-cart-item--container, .product-cart-item, .product-cart-item-price, .cart-total, [data-cart-total-source]');
    }

    shouldProcessMutation(mutation) {
      if (this.isRelevantMutationNode(mutation.target)) {
        return true;
      }

      let changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
      return changedNodes.some((node) => this.isRelevantMutationNode(node));
    }

    releaseCalculatingIfReady(total, force = false) {
      if (!this.isWholesale || force || total !== this.initialCartTotal) {
        this.classList.remove('is-calculating');
      }
    }

    queueUpdate() {
      if (this.updateQueued) return;

      this.updateQueued = true;
      this.updateTimer = setTimeout(() => {
        this.updateQueued = false;
        this.updateTimer = null;
        let total = this.update();
        this.releaseCalculatingIfReady(total);
      }, 120);
    }

    observeCartTotal() {
      let container = this.getCartContainer();
      if (!container) return;

      this.observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => this.shouldProcessMutation(mutation))) {
          this.queueUpdate();
        }
      });
      this.observer.observe(container, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    waitForWholesaleDiscount() {
      let displayedTotal = this.getCurrentTotal();

      if (displayedTotal !== null && displayedTotal !== this.initialCartTotal) {
        this.classList.remove('is-calculating');
        return;
      }

      this.releaseCalculatingTimer = setTimeout(() => {
        let total = this.update();
        this.releaseCalculatingIfReady(total, true);
      }, 3200);
    }

    update() {
      let total = this.getCurrentTotal();
      let minimum = this.minimum;
      let percentage = 1;
      let qualified = total >= minimum;
      let remaining = Math.max(minimum - total, 0);

      if (!Number.isFinite(total) || !Number.isFinite(minimum) || minimum <= 0) {
        return total;
      }

      if (!qualified) {
        percentage = Math.max(total / minimum, 0);

        if (this.amountText && remaining !== this.lastRemaining) {
          let format = window.theme.settings.money_with_currency_format || "${{amount}}";
          this.amountText.innerHTML = formatMoney(remaining, format);
        }
      }

      if (qualified !== this.lastQualified) {
        this.remainingText.style.display = qualified ? 'none' : 'block';
        this.fullText.style.display = qualified ? 'block' : 'none';
      }

      percentage = Math.min(percentage, 1);

      if (total !== this.lastTotal) {
        this.style.setProperty('--percentage', percentage);
      }

      this.lastTotal = total;
      this.lastRemaining = remaining;
      this.lastQualified = qualified;

      return total;
    }
  }
  customElements.define('free-shipping', FreeShipping);
}
